import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Form, Input } from 'components/forms'
import { InfoButton } from 'components/buttons'
import Image from 'components/image'
import { saveUser } from 'registry/api'
import css from './AcceptPartnerInviteForm.scss'

interface AcceptPartnerInviteFormFields {
  password: string
}

interface AcceptPartnerInviteFormProps {
  authToken: string
  onSubmitSuccess: () => void
}

const AcceptPartnerInviteForm: FC<AcceptPartnerInviteFormProps> = ({
  authToken,
  onSubmitSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false)
  const [formSubmissionFailed, setFormSubmissionFailed] = useState(false)
  const { register, handleSubmit, errors } =
    useForm<AcceptPartnerInviteFormFields>()

  const onSubmit = async (data: AcceptPartnerInviteFormFields) => {
    setSubmitting(true)
    setFormSubmissionFailed(false)
    const userData = {
      user: {
        password: data.password,
        isEmailUser: true,
      },
      auth_token: authToken,
    }
    try {
      const response = await saveUser(userData)

      if (response.error) {
        setSubmitting(false)
        setFormSubmissionFailed(true)
      } else {
        setSubmitting(false)
        onSubmitSuccess()
      }
    } catch {
      setSubmitting(false)
      setFormSubmissionFailed(true)
    }
  }

  const generatePasswordErrorMessage = (type: string) => {
    if (type === 'required') {
      return (
        <span className={css.AcceptPartnerInviteForm__helpOrErrorMessage}>
          <Image
            alt="Warning Triangle"
            height={18}
            src="https://images.babylist.com/image/upload/f_auto,q_auto/v1694022585/Onboarding/triangle_danger.png"
            width={18}
          />
          Password must be at least 8 characters
        </span>
      )
    }
    if (type === 'minLength') {
      return (
        <span className={css.AcceptPartnerInviteForm__helpOrErrorMessage}>
          <Image
            alt="Warning Triangle"
            height={18}
            src="https://images.babylist.com/image/upload/f_auto,q_auto/v1694022585/Onboarding/triangle_danger.png"
            width={18}
          />
          Minimum length is 8 characters
        </span>
      )
    }
    // This shouldn't ever happen
    return 'Password is invalid'
  }

  const generateFormErrorMessage = () => {
    if (!authToken)
      return 'Session expired. Please use the link in your invitation email.'
    if (formSubmissionFailed)
      return 'There was an error saving your password. Please contact customer support if this issue continues.'
    return false
  }

  return (
    <Form
      error={generateFormErrorMessage()}
      className="container-md"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        autocomplete="new-password"
        disabled={!authToken}
        type="password"
        label="Password"
        help="Passwords must be at least 8 characters"
        name="password"
        inputRef={register({ required: true, minLength: 8 })}
        error={
          errors.password && generatePasswordErrorMessage(errors.password.type)
        }
      />
      <InfoButton
        disabled={!authToken || submitting}
        type="submit"
        submitting={submitting}
        pill
        block
      >
        Set Password
      </InfoButton>
    </Form>
  )
}

export default AcceptPartnerInviteForm
